import { StyledButton } from "@aureskonnect/react-ui";
import { DEFAULT_PASSCODE } from "@constants";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";
import Modal from "react-modal";

import { store } from "@store";

import {
    handleLedControl,
    handleLoginLocalSettingsButtonOnClickEvent,
    handleLoginSynchronizationButtonOnClickEvent,
} from "@helpers/general";

import {
    setIsSyncLocalModalOpened,
    setLoginKeyboardInputsValues,
    setSelectedShopApp,
    syncStore,
} from "@components/Synchronization/store";
import { UsualNumericKeyboardWrapper } from "@components/UsualNumericKeyboardWrapper";

import {
    kioskStore,
    setIsAuthenticationModalOpened,
    setIsKeyBoardOpened,
    setIsLocalConfigModalOpened,
    setLocalProject,
    setNumericKeyboardInputValue,
} from "@pages/Kiosk/store";

import { version } from "../../../../package.json";
import { ErrorLogInModalWrapper } from "../ErrorLogInModalWrapper";
import { LocalSettingsHeader } from "../LocalSettingsHeader";
import { UsualKeyboardWrapper } from "../UsualKeyboardWrapper";
import AuthenticatedUserList from "./AuthenticatedUserList";

type inputsType = {
    email: string;
    password: string;
};

export function AuthenticationModalWrapper(): JSX.Element {
    const {
        isAuthenticationModalOpened,
        connectedLedPort,
        isLedActive,
        numericKeyboardInputValue,
        project,
        isKeyBoardOpened,
    } = useSnapshot(kioskStore);
    const { loginKeyboardInputsValues } = useSnapshot(syncStore);
    const { isAppSynchronized } = useSnapshot(store);
    // eslint-disable-next-line
    const keyboard = React.useRef<any>();
    const [isErrorLogInModalWrapperOpened, setIsErrorLogInModalWrapperOpened] =
        React.useState<boolean>(false);
    const [loginButton, setLoginButton] = React.useState<string>("Login");

    const [isUserListModelOpen, setIsUserListModelOpen] =
        React.useState<boolean>(false);
    const [selectedUser, setSelectedUser] = React.useState<string | null>(null);
    const [isAccessFromUserList, setIsAccessFromUserList] =
        React.useState<boolean>(false);
    const [isCleared, setIsCleared] = React.useState<boolean>(false);
    const { selectedShopApp } = useSnapshot(syncStore);

    const [inputs, setInputs] = React.useState<inputsType>({
        email: "",
        password: "",
    });

    const [displayedInputs, setDisplayedInputs] = React.useState<inputsType>({
        email: "",
        password: "",
    });

    const encodedEmail = encodeURIComponent(loginKeyboardInputsValues["email"]);
    const encodedPassword = encodeURIComponent(
        loginKeyboardInputsValues["password"]
    );
    const disableConnectButton = areAllValuesFilled(loginKeyboardInputsValues);
    const authenticationTitle =
        !isKeyBoardOpened && isAppSynchronized
            ? t("Access to local settings")
            : t("Connection");
    const loginBtn =
        !isKeyBoardOpened && isAppSynchronized
            ? t("Email authentication")
            : t(loginButton);
    async function handleOnClickLoginEvent() {
        if (isKeyBoardOpened === false && isAppSynchronized) {
            setIsKeyBoardOpened(true);
        } else {
            if (loginButton === "Login") {
                setLoginButton("Connecting");
                if (isAppSynchronized) {
                    const isUserLogged =
                        await handleLoginLocalSettingsButtonOnClickEvent(
                            encodedEmail,
                            encodedPassword,
                            setIsErrorLogInModalWrapperOpened,
                            setLoginButton
                        );
                    if (isUserLogged) {
                        setIsAccessFromUserList(false);
                        setInputs({
                            email: inputs.email,
                            password: "",
                        });
                        setDisplayedInputs({
                            email: `${inputs.email.substring(
                                0,
                                3
                            )}${inputs.email
                                .substring(3)
                                .replaceAll(/[a-zA-Z0-9._%+-]/g, "*")}`,
                            password: "",
                        });
                    } else {
                        setInputs({
                            email: loginKeyboardInputsValues["email"],
                            password: loginKeyboardInputsValues["password"],
                        });
                        setDisplayedInputs({
                            email: loginKeyboardInputsValues["email"],
                            password: loginKeyboardInputsValues["password"],
                        });
                        setSelectedUser(null);
                        if (keyboard.current) {
                            keyboard.current.setInput(
                                loginKeyboardInputsValues["email"],
                                "email"
                            );
                            keyboard.current.setInput(
                                loginKeyboardInputsValues["password"],
                                "password"
                            );
                        }
                    }
                } else {
                    const isUserLogged =
                        await handleLoginSynchronizationButtonOnClickEvent(
                            encodedEmail,
                            encodedPassword,
                            setIsErrorLogInModalWrapperOpened,
                            setLoginButton
                        );
                    if (isUserLogged) {
                        setInputs({
                            email: "",
                            password: "",
                        });
                        setNumericKeyboardInputValue("");
                        setLoginKeyboardInputsValues({
                            email: "",
                            password: "",
                        });
                    }
                }
            } else {
                return;
            }
        }
    }

    const handleUserSelectionValidation = () => {
        if (selectedUser !== null) {
            setIsUserListModelOpen(false);

            setInputs({
                ...inputs,
                email: selectedUser as string,
            });
            setDisplayedInputs({
                ...inputs,
                email: `${(selectedUser as string).substring(0, 3)}${(
                    selectedUser as string
                )
                    .substring(3)
                    .replaceAll(/[a-zA-Z0-9._%+-]/g, "*")}`,
            });
            setLoginKeyboardInputsValues({
                ...inputs,
                email: selectedUser as string,
            });

            setIsAccessFromUserList(true);
        }
        setIsCleared(false);
    };

    const handleonClickBack = () => {
        if (isKeyBoardOpened === true) {
            setIsKeyBoardOpened(false);
        } else {
            setIsAccessFromUserList(false);
            if (!isAppSynchronized) {
                setIsSyncLocalModalOpened(true);
            }
            setIsAuthenticationModalOpened(false);
            setLoginKeyboardInputsValues({
                email: "",
                password: "",
            });
            setInputs({ email: "", password: "" });
            setDisplayedInputs({ email: "", password: "" });
            setIsAccessFromUserList(false);
        }
        setNumericKeyboardInputValue("");
        setSelectedUser(null);
    };

    function areAllValuesFilled(obj: { [x: string]: string }): boolean {
        for (const key in obj) {
            if (obj[key].trim() === "" && key !== "") {
                return true;
            }
        }
        return false;
    }

    function handelValidateOnclick() {
        if (
            numericKeyboardInputValue === selectedShopApp?.passcode ||
            (selectedShopApp.passcode === undefined &&
                numericKeyboardInputValue === DEFAULT_PASSCODE)
        ) {
            if (selectedShopApp.passcode === undefined) {
                setSelectedShopApp({
                    ...selectedShopApp,
                    passcode: DEFAULT_PASSCODE,
                });
            }
            setLocalProject(project);
            setIsAuthenticationModalOpened(false);
            setIsLocalConfigModalOpened(true);
            console.log("User Connected to local Setting using valid button");
        } else {
            setIsErrorLogInModalWrapperOpened(true);
            console.log(
                "Error on user connection to local Setting with valid button"
            );
        }
    }

    React.useEffect(() => {
        if (isLedActive && isAuthenticationModalOpened) {
            handleLedControl(connectedLedPort, "Orange");
        }
        setLoginButton("Login");
        setDisplayedInputs({ email: "", password: "" });
    }, [isAuthenticationModalOpened]);

    return (
        <>
            <ErrorLogInModalWrapper
                setIsErrorLogInModalWrapperOpened={
                    setIsErrorLogInModalWrapperOpened
                }
                isErrorLogInModalWrapperOpened={isErrorLogInModalWrapperOpened}
            />
            {isUserListModelOpen ? (
                <AuthenticatedUserList
                    isUserListModelOpen={isUserListModelOpen}
                    setIsUserListModelOpen={setIsUserListModelOpen}
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                    handleUserSelectionValidation={
                        handleUserSelectionValidation
                    }
                    keyboard={keyboard}
                />
            ) : null}
            <Modal
                isOpen={isAuthenticationModalOpened}
                ariaHideApp={false}
                style={{
                    overlay: {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 2,
                    },
                    content: {
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                        color: "black",
                        height: "auto",
                        maxHeight: "90vh",
                        maxWidth: "90vw",
                        zIndex: 2,
                        backgroundColor: "#FFFFFF",
                        filter: isErrorLogInModalWrapperOpened
                            ? "blur(5px)"
                            : "none",
                        border: "none",
                        borderRadius: "20px",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        bottom: "unset",
                        top: "unset",
                        right: "unset",
                        left: "unset",
                        overflowX: "hidden",
                        overflowY: "auto",
                    },
                }}
            >
                {/* Header */}

                {/* Main Card */}
                <div
                    style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "20px",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        border: "1px solid #e5e7eb",
                        padding: "3rem",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        width: "60rem",
                    }}
                >
                    <LocalSettingsHeader />
                    {/* Profile Animation */}
                    <div
                        style={{
                            textAlign: "center",
                            marginBottom: "2rem",
                        }}
                    >
                        <div
                            style={{
                                height: "120px",
                                width: "120px",
                                margin: "0 auto",
                                borderRadius: "50%",
                                background:
                                    "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                boxShadow:
                                    "0 10px 25px -5px rgba(7, 125, 188, 0.3)",
                            }}
                        >
                            {/* Animated border */}
                            <div
                                style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "-8px",
                                    right: "-8px",
                                    bottom: "-8px",
                                    border: "2px solid transparent",
                                    borderTop: "2px solid #65b6d5",
                                    borderRadius: "50%",
                                    animation:
                                        "profileRotate 3s linear infinite",
                                }}
                            />

                            {/* Profile Icon */}
                            <div
                                style={{
                                    fontSize: "40px",
                                    color: "#ffffff",
                                    animation:
                                        "profilePulse 2s ease-in-out infinite",
                                }}
                            >
                                👤
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <div
                        style={{
                            textAlign: "center",
                            marginBottom: "2rem",
                        }}
                    >
                        <h1
                            style={{
                                font: "normal 28px/34px Segoe UI",
                                letterSpacing: "0px",
                                color: "#1f2937",
                                opacity: 1,
                                marginBottom: "0.5rem",
                                fontWeight: "600",
                            }}
                        >
                            {authenticationTitle}
                        </h1>
                        <p
                            style={{
                                color: "#65b6d5",
                                fontSize: "16px",
                                margin: 0,
                            }}
                        >
                            {t("Please authenticate to continue")}
                        </p>
                    </div>

                    {/* Input Section */}
                    <div
                        style={{
                            flex: 1,
                            marginBottom: "2rem",
                            alignSelf: "center",
                        }}
                    >
                        {!isKeyBoardOpened && isAppSynchronized ? (
                            <div style={{ textAlign: "center" }}>
                                <UsualNumericKeyboardWrapper
                                    isLocalSetting={true}
                                />

                                <StyledButton
                                    style={{
                                        width: "20rem",
                                        height: "60px",
                                        fontSize: "18px",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "12px",
                                        borderRadius: "12px",
                                        border: "none",
                                        boxShadow:
                                            "0 10px 25px -5px rgba(85, 115, 153, 0.4)",
                                        transition: "all 0.2s ease",
                                        background:
                                            "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                                        margin: "1rem auto 0",
                                    }}
                                    rounded={true}
                                    onClick={handelValidateOnclick}
                                >
                                    {t("Validate")}
                                </StyledButton>
                            </div>
                        ) : (
                            <UsualKeyboardWrapper
                                setIsErrorLogInModalWrapperOpened={
                                    setIsErrorLogInModalWrapperOpened
                                }
                                setLoginButton={setLoginButton}
                                setIsUserListModelOpen={setIsUserListModelOpen}
                                inputs={inputs}
                                setInputs={setInputs}
                                displayedInputs={displayedInputs}
                                setDisplayedInputs={setDisplayedInputs}
                                isAccessFromUserList={isAccessFromUserList}
                                handleOnClickLoginEvent={
                                    handleOnClickLoginEvent
                                }
                                keyboard={keyboard}
                                isCleared={isCleared}
                                setIsCleared={setIsCleared}
                                selectedUser={selectedUser}
                            />
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div
                        style={{
                            display: "flex",
                            gap: "16px",
                            justifyContent: "center",
                            marginTop: "auto",
                        }}
                    >
                        <StyledButton
                            style={{
                                width: "180px",
                                height: "60px",
                                fontSize: "18px",
                                fontWeight: "500",
                                borderRadius: "12px",
                                backgroundColor: "#ffffff",
                                border: "2px solid #e5e7eb",
                                color: "#374151",
                                transition: "all 0.2s ease",
                            }}
                            rounded={true}
                            onClick={handleonClickBack}
                        >
                            {t("Back")}
                        </StyledButton>

                        <StyledButton
                            style={{
                                width: "240px",
                                height: "60px",
                                fontSize: "18px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "12px",
                                borderRadius: "12px",
                                border: "none",
                                boxShadow: (
                                    isKeyBoardOpened === false
                                        ? false
                                        : disableConnectButton
                                )
                                    ? "none"
                                    : "0 10px 25px -5px rgba(85, 115, 153, 0.4)",
                                transition: "all 0.2s ease",
                                background: (
                                    isKeyBoardOpened === false
                                        ? false
                                        : disableConnectButton
                                )
                                    ? "#9ca3af"
                                    : "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                                opacity: (
                                    isKeyBoardOpened === false
                                        ? false
                                        : disableConnectButton
                                )
                                    ? 0.6
                                    : 1,
                            }}
                            disabled={
                                isKeyBoardOpened === false
                                    ? false
                                    : disableConnectButton
                            }
                            loading={loginButton === "Connecting"}
                            rounded={true}
                            onClick={handleOnClickLoginEvent}
                        >
                            {loginButton === "Connecting" && (
                                <div
                                    style={{
                                        width: "20px",
                                        height: "20px",
                                        border: "2px solid #ffffff",
                                        borderTop: "2px solid transparent",
                                        borderRadius: "50%",
                                        animation: "spin 1s linear infinite",
                                    }}
                                />
                            )}
                            {loginBtn}
                        </StyledButton>
                    </div>
                    {/* Footer */}
                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                        <p
                            style={{
                                fontSize: "12px",
                                color: "#9ca3af",
                                margin: 0,
                            }}
                        >
                            {`Version ${version}`}
                        </p>
                    </div>
                </div>

                {/* CSS Animations */}
                <style>{`
        @keyframes profileRotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        
        @keyframes profilePulse {
            0%, 100% { 
                transform: scale(1);
                opacity: 1;
            }
            50% { 
                transform: scale(1.1);
                opacity: 0.8;
            }
        }
        
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `}</style>
            </Modal>
        </>
    );
}
